'use client'

import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'

import { Button } from '@/core/components/form/button/button'
import Spinner from '@/core/components/spinner'

import { ItemArt } from '@/features/veluvit/components/ui/item-art'
import EmptyState from '@/features/veluvit/components/ui/empty-state'
import {
  SheetHead,
  TextLink,
} from '@/features/veluvit/components/overlay/layer-parts'
import SheetNav, {
  useSheetNav,
} from '@/features/veluvit/components/overlay/sheet-nav'
import GemCount from '@/features/veluvit/components/ui/gem-count'
import ChargeProduct from '@/features/veluvit/components/wallet/charge-product'
import BillingMethodsPanel from '@/features/veluvit/components/wallet/billing-methods'
import PaymentHistoryPanel from '@/features/veluvit/components/wallet/payment-history'
import PaymentCheckoutForm from '@/features/veluvit/components/wallet/payment-checkout-form'
import { chargeArt } from '@/features/veluvit/lib/charge-products'
import { openAuth } from '@/features/veluvit/services/account.actions'
import { readPaymentProducts } from '@/features/veluvit/services/payment.service'
import {
  openCharge,
  openHistory,
} from '@/features/veluvit/services/wallet.actions'
import { useSessionStore } from '@/features/veluvit/stores/session.store'

import type {
  PaymentProduct,
  PaymentProducts,
} from '@/features/veluvit/schemas/payment.schema'

function Products({ ownerId }: { ownerId: string }) {
  const nav = useSheetNav()
  const [data, setData] = useState<PaymentProducts | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [reload, setReload] = useState(0)
  const selecting = useRef(false)
  const previousDepth = useRef(nav?.depth ?? 0)
  const available = (data?.products ?? []).filter(
    (item) => item.active !== false
  )
  const charges = available
    .filter((item) => item.kind !== 'subscription')
    .sort((a, b) => a.amount - b.amount || a.id.localeCompare(b.id))
  const welcome = charges.filter(
    (item) => item.firstCharge?.eligible && item.firstCharge.bonus > 0
  )
  const firstPercent = welcome[0]?.firstCharge?.percent
  const welcomePercent =
    firstPercent !== undefined &&
    firstPercent > 0 &&
    welcome.every((item) => item.firstCharge?.percent === firstPercent)
      ? firstPercent
      : undefined

  useEffect(() => {
    const depth = nav?.depth ?? 0
    if (depth === 0 && previousDepth.current > 0) {
      selecting.current = false
      setReload((value) => value + 1)
    }
    previousDepth.current = depth
  }, [nav?.depth])

  const [seenReload, setSeenReload] = useState(reload)
  if (seenReload !== reload) {
    setSeenReload(reload)
    setLoading(true)
    setError('')
  }

  useEffect(() => {
    const controller = new AbortController()
    const identity = useSessionStore.getState().identity
    readPaymentProducts(controller.signal)
      .then((result) => {
        if (
          controller.signal.aborted ||
          useSessionStore.getState().identity !== identity
        )
          return
        setData(result)
      })
      .catch((cause: unknown) => {
        if (!controller.signal.aborted)
          setError(
            cause instanceof Error ? cause.message : '상품을 불러오지 못했어요.'
          )
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [ownerId, reload])

  function openCheckout(product: PaymentProduct) {
    if (!nav || selecting.current) return
    selecting.current = true
    const tier = charges.findIndex((item) => item.id === product.id) + 1
    nav.push({
      key: `charge-checkout:${product.id}`,
      render: () => (
        <>
          <SheetHead title="결제" />
          <PaymentCheckoutForm
            ownerId={ownerId}
            product={product}
            catalog={data ?? undefined}
            illustrationTier={tier > 0 ? tier : undefined}
          />
        </>
      ),
    })
  }

  return (
    <section aria-label="충전 상품">
      {loading && !data && (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      )}
      {error && (
        <div className="space-y-3 py-3">
          <p role="alert" className="text-c14 leading-6 text-error">
            {error}
          </p>
          <Button
            variant="secondary"
            onClick={() => setReload((value) => value + 1)}
          >
            다시 확인
          </Button>
        </div>
      )}
      {data && charges.length === 0 && (
        <EmptyState compact title="구매할 수 있는 상품이 없어요" />
      )}
      {welcome.length > 0 && (
        <section
          aria-label="첫 충전 혜택"
          className="relative isolate mt-3 mb-4 flex min-h-22 items-center border-b border-foreground/10 pb-4 over10:mt-5 over10:mb-5 over10:min-h-25"
        >
          <div className="relative z-[1] pr-24 over10:pr-28">
            <p className="text-c12 font-medium text-description over10:text-c14">
              첫 충전 혜택
            </p>
            <h2 className="mt-1 flex flex-wrap items-baseline gap-x-2 text-c32 font-extrabold leading-10 tabular-nums text-sub over10:text-c38 over10:leading-11">
              {welcomePercent !== undefined ? `+${welcomePercent}%` : '보너스'}
              <span className="text-c15 font-semibold text-foreground over10:text-c18">
                추가 젬
              </span>
            </h2>
          </div>
          <Image
            src={chargeArt(6)}
            alt=""
            aria-hidden="true"
            width={512}
            height={512}
            sizes="(min-width: 1001px) 128px, 112px"
            className="pointer-events-none absolute -top-4 -right-1 size-28 object-contain over10:-top-5 over10:right-0 over10:size-32"
          />
        </section>
      )}
      {charges.length > 0 && (
        <>
          <h2 className="sr-only">젬 충전 상품</h2>
          <ul className="grid grid-cols-2 gap-2.5 over10:grid-cols-3 over10:gap-3">
            {charges.map((item, index) => (
              <ChargeProduct
                key={item.id}
                product={item}
                tier={index + 1}
                disabled={false}
                onSelect={() => openCheckout(item)}
              />
            ))}
          </ul>
        </>
      )}
      <div className="mt-5 flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
        <TextLink onClick={openHistory}>젬 사용 내역</TextLink>
        <TextLink
          onClick={() =>
            nav?.push({
              key: 'payment-history',
              render: () => (
                <>
                  <SheetHead title="결제 내역" />
                  <PaymentHistoryPanel ownerId={ownerId} />
                </>
              ),
            })
          }
        >
          결제 내역
        </TextLink>
        <TextLink
          onClick={() =>
            nav?.push({
              key: 'registered-cards',
              render: () => (
                <>
                  <SheetHead title="카드 등록·관리" />
                  <BillingMethodsPanel ownerId={ownerId} />
                </>
              ),
            })
          }
        >
          카드 등록·관리
        </TextLink>
      </div>
    </section>
  )
}

function ChargeHome() {
  const user = useSessionStore((state) => state.data?.user)
  return (
    <>
      <SheetHead title="충전소">
        <span className="mr-1 inline-flex h-8 items-center gap-1 rounded-full bg-sub/10 pr-3 pl-2">
          <span className="sr-only">보유 젬 </span>
          <ItemArt name="gem" px={64} className="size-4.5" />
          <GemCount className="text-c14 font-bold leading-5 tabular-nums text-sub over10:text-c15" />
        </span>
      </SheetHead>
      {user && !user.guest ? (
        <Products key={user.id} ownerId={user.id} />
      ) : (
        <div className="mt-7 space-y-3">
          <p className="text-c14 leading-6 text-description">
            로그인한 계정으로 충전하고 결제 내역을 확인할 수 있어요.
          </p>
          <Button
            className="w-full"
            onClick={() => openAuth('login', openCharge)}
          >
            로그인하고 충전하기
          </Button>
        </div>
      )}
    </>
  )
}

export default function ChargeSheet() {
  return (
    <SheetNav root={{ key: 'charge-home', render: () => <ChargeHome /> }} />
  )
}
